import React, { useEffect, useRef } from 'react';
import type { SubNavItem } from '../../../design-system/v2';
import './flowNav.css';

export interface FlowNavProps<T extends string = string> {
  items: SubNavItem<T>[];
  value: T | false;
  onChange: (value: T) => void;
  ariaLabel: string;
}

/**
 * Section tabs drawn as a flow: tabs before the current one read as done, and
 * the connector leading into the current tab carries a travelling pulse.
 */
export function FlowNav<T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
}: FlowNavProps<T>): React.ReactElement {
  const current = value === false ? -1 : items.findIndex((item) => item.value === value);
  const currentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    currentRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [current]);

  return (
    <nav className="flownav" aria-label={ariaLabel}>
      <ol className="fn-list">
        {items.map((item, i) => {
          const isCurrent = i === current;
          const stepClass = ['fn-step', i < current && 'past', i === current - 1 && 'lead']
            .filter(Boolean)
            .join(' ');
          return (
            <li key={item.value} className={stepClass}>
              <button
                type="button"
                className="fn"
                ref={isCurrent ? currentRef : undefined}
                aria-current={isCurrent ? 'page' : undefined}
                disabled={item.disabled}
                title={item.title}
                onClick={() => onChange(item.value)}
              >
                {item.icon && <span className="fn-ico">{item.icon}</span>}
                <span className="fn-lb">{item.label}</span>
              </button>
              {i < items.length - 1 && (
                <span className="fn-wire" aria-hidden>
                  <i />
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default FlowNav;
